// Import regenerator-runtime for older dependencies (like react-table v7) that need it
// Your own code uses native ES2020 async/await, but some libraries were transpiled with Babel
import 'regenerator-runtime/runtime'
import './i18n'
import { createRoot } from 'react-dom/client'
import Auth0 from 'providers/Auth0'
import Initiation from 'providers/Initiation'
import Routes from 'providers/Routes'
import Recoil from 'providers/Recoil'
import SentryProvider from 'providers/SentryProvider'
import '@messenger/lotus/assets/css/fonts.css'
import '@messenger/lotus/assets/css/google-fonts.css'
import '@messenger/lotus/assets/css/script-fallback.css'
import 'assets/css/sentry-feedback.css'
import * as FullStory from '@fullstory/browser'
import { datadogLogs } from '@datadog/browser-logs'
import Theme from 'providers/Theme'
import reportWebVitals from 'utils/global/reportWebVitals'
import PostHogWrapper from 'providers/PostHog'
import i18n from 'i18next'
import { initSentryWithCurrentLanguage } from 'utils/initSentry'
import { DEFAULT_LOCALE_CODE } from '@messenger/consts'
import { isLocal } from 'utils/global/env'
import { scrubLogsEvent } from '@/utils/global/scrubTrackedUrl'
import { installEditorTextGeometryProbe } from '@/utils/editor/textGeometryProbe'
import { installEditorTextBoxSheets } from '@/utils/editor-text-box/editorTextBoxAdapter'

// Before React mounts, so the layered Lotus sheets never share a style
// update with a styled-components rewrite (see installEditorTextBoxSheets).
installEditorTextBoxSheets()

if (!isLocal) {
  FullStory.init({ orgId: process.env.REACT_APP_FULLSTORY_ORG_ID as string })
}

// Set language before initializing Sentry and rendering the app
const setInitialLanguage = async () => {
  const params = new URLSearchParams(window.location.search)
  const queryLang = params.get('lang')
  const localStorageLang = localStorage.getItem('language')
  const lang = queryLang || localStorageLang || DEFAULT_LOCALE_CODE
  if (i18n.language !== lang) {
    await i18n.changeLanguage(lang)
  }
}

setInitialLanguage().then(() => {
  if (!isLocal) {
    initSentryWithCurrentLanguage()
  }

  if (!isLocal) {
    datadogLogs.init({
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN as string,
      site: 'datadoghq.com',
      service: `mdl-frontend-${process.env.REACT_APP_ENV || 'development'}`,
      env: process.env.REACT_APP_ENV || 'development',
      forwardErrorsToLogs: false,
      sessionSampleRate: 100,
      beforeSend: scrubLogsEvent,
    })
  }

  installEditorTextGeometryProbe()
  const rootElement = document.getElementById('root')
  if (!rootElement) throw new Error('Failed to find the root element')
  const root = createRoot(rootElement)

  ;(async () => {
    root.render(
      <PostHogWrapper>
        <Recoil>
          <Theme>
            <Initiation>
              <Auth0>
                <SentryProvider>
                  <Routes />
                </SentryProvider>
              </Auth0>
            </Initiation>
          </Theme>
        </Recoil>
      </PostHogWrapper>,
    )
  })()

  reportWebVitals(() => {})
})
